import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const ServicesSection = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const categories = [
    { id: 'all', label: 'All Services', icon: 'Grid3X3' },
    { id: 'design', label: 'Design', icon: 'Palette' },
    { id: 'development', label: 'Development', icon: 'Code' },
    { id: 'marketing', label: 'Marketing', icon: 'TrendingUp' },
    { id: 'content', label: 'Content', icon: 'PenTool' }
  ];

  useEffect(() => {
    const fetchLatestJobs = async () => {
      try {
        const response = await fetch('http://localhost/phpValidation/freeuser-landing/latest_job.php?action=latest');
        const data = await response.json();
        
        if (data.success) {
          setJobs(data.data.map(job => ({
            id: job.job_guid,
            title: job.title,
            category: getCategoryFromDomain(job.domain_id),
            startingPrice: job.budget || 299,
            deliveryTime: job.projectDuration || '5-7 days',
            icon: getIconForJob(job),
            description: job.jobDescription?.substring(0, 100) + '...',
            proposals: job.proposals || 0,
            rating: parseFloat(job.jobposter_rating) || 0,
            companyName: job.companyName,
            profileImg: job.profileimg_url
          })));
        }
      } catch (error) {
        console.error('Error fetching latest jobs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestJobs();
  }, []);

  const getCategoryFromDomain = (domainId) => {
    const categories = {
      1: 'development',
      2: 'development',
      3: 'marketing',
      4: 'content',
      5: 'design'
    };
    return categories[domainId] || 'development';
  };

  const getIconForJob = (job) => {
    const icons = {
      development: ['Code', 'Globe', 'Database'],
      design: ['Palette', 'PenTool', 'Image'],
      marketing: ['TrendingUp', 'Megaphone', 'BarChart2'],
      content: ['FileText', 'Edit3', 'Type']
    };
    const category = getCategoryFromDomain(job.domain_id);
    const options = icons[category] || ['Code'];
    return options[Math.floor(Math.random() * options.length)];
  };

  const filteredServices = activeFilter === 'all' 
    ? jobs 
    : jobs.filter(service => service.category === activeFilter);

  if (loading) {
    return (
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading services...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Available
            <span className="text-blue-600"> Services</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Browse our latest job postings from top clients looking for skilled professionals.
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => {
            const categoryCount = category.id === 'all' 
              ? jobs.length 
              : jobs.filter(job => job.category === category.id).length;
            
            return (
              <button
                key={category.id}
                onClick={() => setActiveFilter(category.id)}
                className={`flex items-center px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeFilter === category.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Icon name={category.icon} size={18} className="mr-2" />
                {category.label} ({categoryCount})
              </button>
            );
          })}
        </div>

        {/* Services Grid */}
        {filteredServices.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                {/* Card Header */}
                <div className="p-6 pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center">
                      <Icon 
                        name={service.icon} 
                        size={24} 
                        className="text-blue-600" 
                      />
                    </div>
                    {service.rating > 0 && (
                      <div className="flex items-center bg-yellow-50 px-3 py-1 rounded-full">
                        <Icon name="Star" size={16} className="text-yellow-500 mr-1" />
                        <span className="text-yellow-800 font-medium">{service.rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {service.title}
                  </h3>
                  {/* <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    {service.description}
                  </p> */}

                  {/* Provider Info */}
                  {service.companyName && (
                    <div className="flex items-center mb-4">
                      {service.profileImg ? (
                        <img 
                          src={service.profileImg} 
                          alt={service.companyName}
                          className="w-8 h-8 rounded-full mr-2"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-gray-300 rounded-full mr-2 flex items-center justify-center">
                          <Icon name="Briefcase" size={16} className="text-gray-600" />
                        </div>
                      )}
                      <span className="text-sm text-gray-600">{service.companyName}</span>
                    </div>
                  )}

                  {/* Stats */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm text-gray-600">
                      <Icon name="Users" size={14} className="inline mr-1" />
                      {service.proposals} proposals
                    </div>
                    <div className="text-sm font-semibold text-blue-600">
                      <Icon name="Clock" size={14} className="inline mr-1" />
                      {service.deliveryTime}
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="mb-4">
                    <span className="text-2xl font-bold text-blue-600">
                      ₹{service.startingPrice}
                    </span>
                    <span className="text-gray-500 text-sm ml-1">starting price</span>
                  </div>
                </div>

                {/* CTA Button */}
                <div className="px-6 pb-6">
                  <button className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-300">
                    Apply Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Search" size={32} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No services found</h3>
            <p className="text-gray-600">Try selecting a different category or check back later.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesSection;